import { useEffect, useMemo, useState } from 'react';
import LineChart from '../components/LineChart';
import FieldSelector, { type MeasureKey } from '../components/FieldSelector';
import { loadManifest } from '../lib/dataLoader';

type Scope = { type: 'school' } | { type: 'level', level: number } | { type: 'class', className: string };

export default function CourseAnalysis() {
  const [loading, setLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [scope, setScope] = useState<Scope>({ type: 'school' });
  const [state, setState] = useState<any>(null);
  const [measure, setMeasure] = useState<MeasureKey>('Net');

  useEffect(() => {
    (async () => {
      const m = await loadManifest();
      setState(m);
      setLoading(false);
    })().catch((e) => {
      console.error(e);
      setLoading(false);
    });
  }, []);

  const chart = useMemo(() => {
    if (!state) return null;
    const m = state as any;
    const times = m.times as { key: string; label: string }[];
    const categories = times.map(t => t.label);
    const agg = scope.type === 'school' ? m.school : scope.type === 'class' ? m.classes[scope.className] : m.classLevels[String(scope.level)];
    if (!agg) return null;

    const series: { name: string; data: number[] }[] = [];
    for (const code of Array.from(selectedFields)) {
      series.push({ name: code, data: times.map(t => (agg.times[t.key]?.fields?.[code]?.[measure] ?? 0)) });
    }

    return { categories, series };
  }, [state, selectedFields, measure, scope]);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-800">Ders Analiz</h1>
      {loading && <div className="text-sm text-slate-600">Veriler yükleniyor...</div>}
      {!loading && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-lg border bg-white p-4">
              <div className="mb-2 text-sm font-medium">Kapsam</div>
              <div className="flex flex-col gap-2 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="radio" name="scope" checked={scope.type==='school'} onChange={() => setScope({ type: 'school' })} />
                  Okul
                </label>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="scope" checked={scope.type==='level'} onChange={() => setScope({ type: 'level', level: 12 })} />
                    Sınıf düzeyi
                  </label>
                  {scope.type==='level' && (
                    <select className="ml-2 rounded-md border px-2 py-1" value={scope.level} onChange={(e) => setScope({ type: 'level', level: Number(e.target.value) })}>
                      {[9,10,11,12].map(n => <option key={n} value={n}>{n}.sınıf</option>)}
                    </select>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2">
                    <input type="radio" name="scope" checked={scope.type==='class'} onChange={() => setScope({ type: 'class', className: firstClass(state) })} />
                    Şube
                  </label>
                  {scope.type==='class' && (
                    <select className="ml-2 rounded-md border px-2 py-1" value={scope.className} onChange={(e) => setScope({ type: 'class', className: e.target.value })}>
                      {Object.keys(state?.classes ?? {}).map((c: string) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="mb-2 text-sm font-medium">Alan Seçimi</div>
              <FieldSelector
                fields={state?.fieldsIndex ?? []}
                selected={selectedFields}
                onToggle={(code)=>{
                  const next = new Set(selectedFields);
                  next.has(code) ? next.delete(code) : next.add(code);
                  setSelectedFields(next);
                }}
                measure={measure}
                onMeasureChange={setMeasure}
              />
            </div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            {!chart ? (
              <div className="text-sm text-slate-600">Kapsam ve ders(ler) seçin.</div>
            ) : (
              <LineChart categories={chart.categories} series={chart.series} yName="Net" />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function firstClass(state: any): string {
  const keys = Object.keys(state?.classes ?? {});
  return keys[0] ?? '';
}


